import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin } from 'lucide-react';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    shop: [
      { to: '/products?category=vegetables', label: 'Vegetables' },
      { to: '/products?category=fruits', label: 'Fruits' },
      { to: '/products?category=grains', label: 'Grains & Cereals' },
      { to: '/products?category=dairy', label: 'Dairy Products' },
    ],
    support: [
      { to: '/about', label: 'About Us' },
      { to: '/contact', label: 'Contact Us' },
      { to: '/faq', label: 'FAQ' },
      { to: '/shipping', label: 'Shipping Info' },
    ],
    legal: [
      { to: '/terms', label: 'Terms of Service' },
      { to: '/privacy', label: 'Privacy Policy' },
      { to: '/refund', label: 'Refund Policy' },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
    { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
    { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-dark-dark dark:bg-white text-white dark:text-dark-dark transition-colors duration-300">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2 mb-6 group">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <Leaf className="w-7 h-7 text-white" />
              </div>
              <span className="font-display font-bold text-2xl">
                Angel <span className="text-accent dark:text-primary transition-colors duration-300">Cite</span>
              </span>
            </Link>
            <p className="text-gray-300 dark:text-gray-600 mb-6 max-w-sm transition-colors duration-300">
              Fresh agricultural products delivered directly from local farms to your doorstep. 
              Quality you can trust, freshness you can taste.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-gray-300 dark:text-gray-600 transition-colors duration-300">
                <MapPin className="w-5 h-5 text-accent dark:text-primary flex-shrink-0 transition-colors duration-300" />
                <span>123 Farm Road, Harare, Zimbabwe</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300 dark:text-gray-600 transition-colors duration-300">
                <Phone className="w-5 h-5 text-accent dark:text-primary flex-shrink-0 transition-colors duration-300" />
                <span>+263 77 123 4567</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300 dark:text-gray-600 transition-colors duration-300">
                <Mail className="w-5 h-5 text-accent dark:text-primary flex-shrink-0 transition-colors duration-300" />
                <span>info@angelcite.com</span>
              </div>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-accent dark:text-primary transition-colors duration-300">Shop</h3>
            <ul className="space-y-3">
              {footerLinks.shop.map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-gray-300 dark:text-gray-600 hover:text-white dark:hover:text-dark-dark transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-accent dark:text-primary transition-colors duration-300">Support</h3>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-gray-300 dark:text-gray-600 hover:text-white dark:hover:text-dark-dark transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="font-semibold text-lg mb-4 text-accent dark:text-primary transition-colors duration-300">Legal</h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.to}>
                  <Link 
                    to={link.to} 
                    className="text-gray-300 dark:text-gray-600 hover:text-white dark:hover:text-dark-dark transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Newsletter Section */}
      <div className="border-t border-dark-light dark:border-gray-200 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="font-semibold text-lg mb-1">Subscribe to our newsletter</h3>
              <p className="text-gray-400 dark:text-gray-500 text-sm transition-colors duration-300">Get fresh updates and exclusive offers</p>
            </div>
            <form className="flex w-full md:w-auto gap-3">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 md:w-64 px-4 py-2.5 rounded-lg bg-dark-light dark:bg-gray-100 border border-dark dark:border-gray-300 text-white dark:text-dark-dark placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-accent dark:focus:ring-primary transition-all duration-300"
              />
              <button
                type="submit"
                className="px-6 py-2.5 bg-accent dark:bg-primary text-white font-medium rounded-lg hover:bg-accent-dark dark:hover:bg-primary-dark transition-colors duration-300"
              >
                Subscribe
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-dark-light dark:border-gray-200 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 dark:text-gray-500 text-sm text-center md:text-left transition-colors duration-300">
              © {currentYear} Angel Cite. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-dark-light dark:bg-gray-100 flex items-center justify-center text-gray-300 dark:text-gray-600 hover:bg-accent dark:hover:bg-primary hover:text-white transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;